export type Grouping<T, Extras, Key> = {
  key: Key;
  items: T[];
} & (Extras extends never ? {} : Extras);

export function chunkArray<T>(items: T[], chunkSize: number): T[][] {
  const chunks = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    const chunk = items.slice(i, i + chunkSize);
    chunks.push(chunk);
  }
  return chunks;
}

export function groupBy<T, Extras extends {},
  Selector extends ((keyof T) | ((item: T, index: number) => string | number)),
  Key extends (string | number) & (Selector extends (keyof T) ? T[Selector] : string | number)
>(array: T[], selector: Selector, extras?: (item: T) => Extras) {
  const res: Map<Key, Grouping<T, Extras, Key>> = new Map();

  const selectorFn: (item: T, index: number) => Key = typeof selector === 'function' ?
    selector as any :
    ((x: T) => x[selector as (keyof T)]);

  for (let index = 0; index < array.length; index++) {
    const item = array[index];
    const key = selectorFn(item, index);
    const existing = res.get(key);

    if (existing) { existing.items.push(item); }
    else {
      const grouping = {
        key,
        items: [item],
        ...extras?.(item),
      } as Grouping<T, Extras, Key>;

      res.set(key, grouping);
    }
  }

  return res;
}
